import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SpaceCalculatorValues } from '@r6digital/rapidstor-types/types/Site';

import {
    EMPTY_BUSINESS_STORAGE,
    EMPTY_CUSTOM_NEEDS,
    EMPTY_EXTRA_SPACE,
    EMPTY_MOVING_HOUSE,
    SpaceCalculatorResult,
    calculateBusinessStorage,
    calculateCustomNeeds,
    calculateExtraSpace,
    calculateMovingHouse,
} from './calculator';
import StepSlider, { SliderRange } from './StepSlider';
import useSliderInput from './useSliderInput';

import classes from './styles.module.scss';

const ROOMS_RANGE: SliderRange = { max: 8, tickInterval: 1 };
const ITEMS_RANGE: SliderRange = { max: 20, tickInterval: 5 };
const SIZE_RANGE: SliderRange = { max: 10, tickInterval: 2 };

export interface ModeFormProps {
    values: SpaceCalculatorValues;
    onSubmit: (result: SpaceCalculatorResult | null) => void;
}

function StepHeading() {
    const { t } = useTranslation();

    return (
        <div className={classes.stepRow}>
            <div className={classes.stepText}>
                <span>{t('standardUnitListing:spaceCalculator:stepTwo')}</span>
                {' '}
                {t('standardUnitListing:spaceCalculator:stepTwoTitle')}
            </div>
            <span className={classes.helperText}>
                {t('standardUnitListing:spaceCalculator:stepTwoHelp')}
            </span>
        </div>
    );
}

function OrDivider() {
    const { t } = useTranslation();

    return <div className={classes.orDivider}>{t('standardUnitListing:spaceCalculator:or')}</div>;
}

function FindUnitSizeButton({ onClick }: { onClick: () => void }) {
    const { t } = useTranslation();

    return (
        <div className={classes.submitRow}>
            <button type="button" className={classes.submitButton} onClick={onClick}>
                {t('standardUnitListing:spaceCalculator:findUnitSize')}
            </button>
        </div>
    );
}

export function MovingHouseForm({ values, onSubmit }: ModeFormProps) {
    const { t } = useTranslation();
    const [input, setField] = useSliderInput(EMPTY_MOVING_HOUSE);

    return (
        <>
            <StepHeading />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:bedrooms')}
                value={input.bedrooms}
                onChange={setField('bedrooms')}
                range={ROOMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:smallRooms')}
                value={input.smallRooms}
                onChange={setField('smallRooms')}
                range={ROOMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:largeRooms')}
                value={input.largeRooms}
                onChange={setField('largeRooms')}
                range={ROOMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:garages')}
                value={input.garages}
                onChange={setField('garages')}
                range={ROOMS_RANGE}
            />
            <FindUnitSizeButton onClick={() => onSubmit(calculateMovingHouse(input, values))} />
        </>
    );
}

export function ExtraSpaceForm({ values, onSubmit }: ModeFormProps) {
    const { t } = useTranslation();
    const [input, setField] = useSliderInput(EMPTY_EXTRA_SPACE);

    return (
        <>
            <StepHeading />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:rooms')}
                value={input.rooms}
                onChange={setField('rooms')}
                range={ROOMS_RANGE}
            />
            <OrDivider />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:largeItems')}
                value={input.largeItems}
                onChange={setField('largeItems')}
                range={ITEMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:smallItems')}
                value={input.smallItems}
                onChange={setField('smallItems')}
                range={ITEMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:boxes')}
                value={input.boxes}
                onChange={setField('boxes')}
                range={ITEMS_RANGE}
            />
            <FindUnitSizeButton onClick={() => onSubmit(calculateExtraSpace(input, values))} />
        </>
    );
}

export function BusinessStorageForm({ values, onSubmit }: ModeFormProps) {
    const { t } = useTranslation();
    const [input, setField] = useSliderInput(EMPTY_BUSINESS_STORAGE);

    return (
        <>
            <StepHeading />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:boxes')}
                value={input.boxes}
                onChange={setField('boxes')}
                range={ITEMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:smallItems')}
                value={input.smallItems}
                onChange={setField('smallItems')}
                range={ITEMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:largeItems')}
                value={input.largeItems}
                onChange={setField('largeItems')}
                range={ITEMS_RANGE}
            />
            <FindUnitSizeButton onClick={() => onSubmit(calculateBusinessStorage(input, values))} />
        </>
    );
}

export function CustomNeedsForm({
    values,
    sizeSymbol,
    onSubmit,
}: ModeFormProps & { sizeSymbol: string }) {
    const { t } = useTranslation();
    const [input, setField] = useSliderInput(EMPTY_CUSTOM_NEEDS);

    return (
        <>
            <StepHeading />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:width', { sizeSymbol })}
                value={input.width}
                onChange={setField('width')}
                range={SIZE_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:length', { sizeSymbol })}
                value={input.length}
                onChange={setField('length')}
                range={SIZE_RANGE}
            />
            <OrDivider />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:smallItems')}
                value={input.smallItems}
                onChange={setField('smallItems')}
                range={ITEMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:largeItems')}
                value={input.largeItems}
                onChange={setField('largeItems')}
                range={ITEMS_RANGE}
            />
            <StepSlider
                label={t('standardUnitListing:spaceCalculator:boxes')}
                value={input.boxes}
                onChange={setField('boxes')}
                range={ITEMS_RANGE}
            />
            <FindUnitSizeButton onClick={() => onSubmit(calculateCustomNeeds(input, values))} />
        </>
    );
}
